"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { ChatHeader } from "./ChatHeader"
import { ChatInput } from "./ChatInput"
import { ChatMessages } from "./ChatMessages"
import { Attachment, Message } from "./types"

import { useGlobal } from "@/app/context/GlobalContext"
import { useChatStream } from "@/app/context/ChatStreamContext"
import { useBrandConfig } from "@/hooks/useBrandConfig"
import { buildStreamErrorContent } from "@/lib/streamError"
import { setAgentConfig } from "@/services/agentCoreService"
import { submitFeedback } from "@/services/feedbackService"
import { getUploadUrl, uploadFileToS3 } from "@/services/uploadService"
import { useAuth } from "react-oidc-context"
import { KnowledgeFile } from "@/services/projectService"

interface ChatInterfaceProps {
  sessionId: string | null
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  onNewChat: () => void
  onMessagesChange: (messages: Message[], sessionId: string) => void
  onNeedSession: (firstMessage: string) => Promise<string>
  customInstructions?: string
  knowledgeFiles?: KnowledgeFile[]
  projectName?: string
  chatTitle?: string
  abortStreamRef?: React.MutableRefObject<(() => void) | null>
  /** 保存失敗など、会話の継続に関わる注意（親が管理・非 null の間表示し続ける） */
  notice?: string | null
}

export default function ChatInterface({
  sessionId,
  messages,
  setMessages,
  onNewChat,
  onMessagesChange,
  onNeedSession,
  customInstructions,
  knowledgeFiles,
  projectName,
  chatTitle,
  abortStreamRef,
  notice,
}: ChatInterfaceProps) {
  const brand = useBrandConfig()
  const [input, setInput] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [extendedThinking, setExtendedThinking] = useState(false)
  // chatEndpoint 構成（OCI 等の自前ホスティング）では chat 常駐が id_token を検証する
  // （aud=client_id は id_token 側にしか無い）。AWS の AgentCore は従来どおり access_token。
  const [chatUsesIdToken, setChatUsesIdToken] = useState(false)

  const { isLoading, setIsLoading } = useGlobal()
  const stream = useChatStream()
  const auth = useAuth()

  // Ref for message container to enable auto-scrolling
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Ref to track latest messages for saving after streaming
  const messagesRef = useRef<Message[]>(messages)
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  // 旧: ストリーム abort と session tracking のためのローカル ref。
  // 2026-06-21 以降は ChatStreamContext がこれらを ownership を持つので未使用。
  // 後段 sendMessage の旧 watchdog/abort コードと合わせて削除予定。
  // const abortControllerRef = useRef<AbortController | null>(null)
  // const streamingSessionIdRef = useRef<string | null>(null)

  // Load agent configuration on mount
  useEffect(() => {
    async function loadConfig() {
      try {
        const response = await fetch("/aws-exports.json")
        if (!response.ok) {
          throw new Error("Failed to load configuration")
        }
        const config = await response.json()

        // chatEndpoint（例 "/chat"）があれば AgentCore Runtime ARN は不要
        // （リバースプロキシ経由で chat 常駐へ直接届ける・OCI 構成）。
        if (!config.agentRuntimeArn && !config.chatEndpoint) {
          throw new Error("Agent Runtime ARN not found in configuration")
        }

        await setAgentConfig(
          config.agentRuntimeArn,
          config.awsRegion || "us-east-1",
          config.agentPattern,
          config.chatEndpoint
        )
        setChatUsesIdToken(Boolean(config.chatEndpoint))
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        setError(`Configuration error: ${errorMessage}`)
        console.error("Failed to load agent configuration:", err)
      }
    }

    loadConfig()
  }, [])

  // Auto-scroll to bottom when messages change。
  // - セッション切替・初回ロード・自分の新規送信 → 必ず最下部へ
  // - ストリーミング中のチャンク追記 → ユーザーが上へスクロールして読み返して
  //   いる間（最下部から 120px 超）は追従しない（特にモバイルで読み返し不能になる）
  const prevScrollSessionRef = useRef<string | null>(null)
  const prevMessageCountRef = useRef(0)
  useEffect(() => {
    const sessionChanged = prevScrollSessionRef.current !== sessionId
    const wasEmpty = prevMessageCountRef.current === 0 && messages.length > 0
    const lastIsUser = messages[messages.length - 1]?.role === "user"
    prevScrollSessionRef.current = sessionId
    prevMessageCountRef.current = messages.length
    if (!(sessionChanged || wasEmpty || lastIsUser)) {
      const container = messagesEndRef.current?.parentElement?.closest(
        "[data-slot=scroll-area-viewport], .overflow-y-auto, .overflow-auto"
      ) as HTMLElement | null
      if (container) {
        const distanceFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight
        if (distanceFromBottom > 120) return
      }
    }
    scrollToBottom()
  }, [messages, sessionId])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  // Expose abort function to parent via ref（session 切替時に親が叩く）。
  // 実体は ChatStreamProvider の stopStream を呼ぶだけ。部分保存も context の
  // onComplete 経路に集約されているのでここでは特別なことはしない。
  useEffect(() => {
    if (abortStreamRef) {
      abortStreamRef.current = () => {
        stream.stopStream()
        setIsLoading(false)
      }
    }
    return () => { if (abortStreamRef) abortStreamRef.current = null }
  }, [abortStreamRef, setIsLoading, stream])

  // 注: 以前ここにあった "Cleanup on unmount で abortController.abort()" は削除した（2026-06-21）。
  // ストリームは ChatStreamProvider が App ルートで保持するため、ルート遷移
  // (/chat → /admin/knowledge 等) で ChatInterface がアンマウントされても継続する。
  // 明示的中断は session 切替時のみ abortStreamRef → stream.stopStream() で行う。

  // Upload selected files to S3 and return attachment metadata
  const uploadFiles = useCallback(
    async (files: File[]): Promise<Attachment[]> => {
      const idToken = auth.user?.id_token
      if (!idToken) throw new Error("Authentication required")

      const attachments: Attachment[] = []
      for (const file of files) {
        const { uploadUrl, fileKey } = await getUploadUrl(file.name, file.type, idToken)
        await uploadFileToS3(file, uploadUrl)
        attachments.push({
          fileKey,
          filename: file.name,
          contentType: file.type,
        })
      }
      return attachments
    },
    [auth.user]
  )

  // Send message to AgentCore backend with streaming support
  const sendMessage = useCallback(
    async (userMessage: string) => {
      if (!userMessage.trim() && selectedFiles.length === 0) return

      setError(null)

      // If no session yet, create one
      let activeSessionId = sessionId
      if (!activeSessionId) {
        try {
          activeSessionId = await onNeedSession(userMessage || selectedFiles[0]?.name || "New chat")
        } catch (err) {
          console.error("Failed to create session:", err)
          setError("Failed to create session. Please try again.")
          return
        }
      }

      // Upload files first (before clearing state)
      let attachments: Attachment[] = []
      const filesToUpload = [...selectedFiles]
      setSelectedFiles([])
      setInput("")

      if (filesToUpload.length > 0) {
        try {
          setIsLoading(true)
          attachments = await uploadFiles(filesToUpload)
        } catch (err) {
          console.error("Failed to upload files:", err)
          setError("Failed to upload files. Please try again.")
          setIsLoading(false)
          return
        }
      }

      // Add user message to chat
      const newUserMessage: Message = {
        role: "user",
        content: userMessage,
        timestamp: new Date().toISOString(),
        ...(attachments.length > 0 ? { attachments } : {}),
      }

      // 旧: messagesRef.current から previousMessages を計算してから user msg を append。
      // ここで messagesRef を snapshot として保存（次の setMessages の前に取る）。
      const previousMessagesSnapshot = [...messagesRef.current]

      setMessages((prev) => [...prev, newUserMessage])
      setIsLoading(true)

      // Create placeholder for assistant response
      const assistantResponse: Message = {
        role: "assistant",
        content: "",
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, assistantResponse])

      const accessToken = chatUsesIdToken ? auth.user?.id_token : auth.user?.access_token
      const userId = auth.user?.profile?.sub
      if (!accessToken || !userId) {
        setError("Authentication required. Please log in again.")
        setIsLoading(false)
        return
      }

      // ChatStreamProvider に委譲。ルート遷移で abort されないため SSE が継続し、
      // 完了時に onComplete → onMessagesChange で DB 保存。
      await stream.startStream({
        userMessage,
        sessionId: activeSessionId,
        previousMessages: previousMessagesSnapshot,
        userMessageObject: newUserMessage,
        accessToken,
        userId,
        email: auth.user?.profile?.email,
        attachments: attachments.length > 0 ? attachments : undefined,
        customInstructions,
        knowledgeFiles,
        extendedThinking,
        onChunk: (streamedContent: string) => {
          // Optional: ローカル messages の placeholder も更新（同 session マウント中の即時反映）
          // session が切り替わっていたら何もしない（context 側でも guard はあるが二重防御）
          setMessages((prev) => {
            if (prev.length === 0) return prev
            const last = prev[prev.length - 1]
            if (last.role !== "assistant") return prev
            const updated = [...prev]
            updated[updated.length - 1] = { ...last, content: streamedContent }
            return updated
          })
        },
        onComplete: async (finalMessages, sId) => {
          // DB 保存（ChatInterface アンマウント済みでも updateSession API は実行される）
          onMessagesChange(finalMessages, sId)
        },
        onResume: (resumeMsg) => {
          // 自動再開（2026-07-17）: 再開 user 吹き出し＋新しい assistant placeholder を
          // 画面に挿入する。直前の placeholder が空（何も受信できないまま切断）なら
          // 空の吹き出しを残さないよう差し替える。onChunk は常に「最後の assistant」を
          // 更新するので、以降の受信は新 placeholder に流れ込む。
          setMessages((prev) => {
            const next = [...prev]
            const last = next[next.length - 1]
            if (last && last.role === "assistant" && !last.content) {
              next.pop()
            }
            next.push(resumeMsg, {
              role: "assistant",
              content: "",
              timestamp: new Date().toISOString(),
            })
            return next
          })
        },
        onError: async (errorMessage, partialContent) => {
          console.error("Error invoking AgentCore:", errorMessage)
          // partialContent があれば最後の assistant に notice を載せる（マウント中のみ）。
          // 2026-07-14: 内容が届いている時は全画面の赤帯を出さない——サーバ側では処理が
          // 完了しているのに「全部失敗した」ように見えるため（NetSuite 見積登録の実事例）。
          // inline notice（buildStreamErrorContent）だけで足りる。
          if (partialContent) {
            setMessages((prev) => {
              if (prev.length === 0) return prev
              const last = prev[prev.length - 1]
              if (last.role !== "assistant") return prev
              const updated = [...prev]
              updated[updated.length - 1] = {
                ...last,
                content: buildStreamErrorContent(last.content || partialContent),
              }
              return updated
            })
          } else {
            setError(
              "応答を取得できませんでした（接続が中断されました）。もう一度お試しください。"
            )
          }
        },
      })
      setIsLoading(false)
    },
    [sessionId, auth.user, setIsLoading, setMessages, onNeedSession, onMessagesChange, selectedFiles, uploadFiles, customInstructions, knowledgeFiles, extendedThinking, chatUsesIdToken]
  )

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(input)
  }

  // 停止ボタン押下時: ストリームを中断し loading 表示も解除する
  // （abortStreamRef ハンドラ 108-116 と同一ロジック）
  const handleStop = () => {
    stream.stopStream()
    setIsLoading(false)
  }

  // Handle feedback submission
  const handleFeedbackSubmit = async (
    messageContent: string,
    feedbackType: "positive" | "negative",
    comment: string
  ) => {
    if (!sessionId) return
    try {
      const idToken = auth.user?.id_token

      if (!idToken) {
        throw new Error("Authentication required. Please log in again.")
      }

      await submitFeedback(
        {
          sessionId,
          message: messageContent,
          feedbackType,
          comment: comment || undefined,
        },
        idToken
      )

      console.log("Feedback submitted successfully")
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      console.error("Error submitting feedback:", err)
      setError(`Failed to submit feedback: ${errorMessage}`)
    }
  }

  const isInitialState = messages.length === 0
  const hasAssistantMessages = messages.some((message) => message.role === "assistant")

  // 履歴は追記型（別テーブル）になり保存の上限はなくなった（2026-07-02 P2）。
  // ただし超長寿セッションは開くときの読み込みと画面描画が重くなるため、目安を越えたら
  // やわらかく新しいチャットを勧める。
  const MESSAGE_SOFT_LIMIT = 1000
  const limitNotice =
    messages.length >= MESSAGE_SOFT_LIMIT
      ? `このチャットはとても長くなっています（${messages.length}件）。履歴は保存され続けますが、表示が重くなるため、新しいチャットを始めることをおすすめします。`
      : null

  // h-dvh: iOS Safari の 100vh はツールバー非表示時の高さで、表示中は下端の
  // 入力欄がツールバーに隠れる。dvh は実際の可視領域に追従する。
  // <768px は ChatPage 側で下部タブバー（56px＋ホームバー safe-area）を末尾に置くため、
  // その分だけ自身の高さを削って渡す（md 以上はタブバーが無いので従来どおり全高）。
  return (
    <div className="flex flex-col h-[calc(100dvh_-_56px_-_env(safe-area-inset-bottom))] md:h-dvh w-full">
      {/* Fixed header */}
      <div className="flex-none">
        <ChatHeader onNewChat={onNewChat} canStartNewChat={hasAssistantMessages} projectName={projectName} chatTitle={chatTitle} />
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 mt-2">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {notice && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 mt-2">
            <p className="text-sm text-red-700">{notice}</p>
          </div>
        )}
        {limitNotice && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mx-4 mt-2">
            <p className="text-sm text-amber-800">{limitNotice}</p>
          </div>
        )}
        {/* コールドスタート中の案内（従来はリトライが見えず無反応に見えた・2026-07-02 P3） */}
        {stream.connectionNotice && (
          <div className="bg-sky-50 border-l-4 border-sky-500 p-4 mx-4 mt-2">
            <p className="text-sm text-sky-800">{stream.connectionNotice}</p>
          </div>
        )}
      </div>

      {/* Conditional layout based on whether there are messages */}
      {isInitialState ? (
        <>
          <div className="grow" />
          <div className="text-center mb-6">
            {brand.welcomeLogo ? (
              <img src={`/${brand.welcomeLogo}`} alt={brand.appTitle} className="max-w-md mx-auto" />
            ) : (
              <h2 className="text-2xl font-bold text-gray-800">{brand.welcomeMessage}</h2>
            )}
            <p className="text-gray-600 mt-2">答えるだけでなく、実行するパートナーです。</p>
          </div>
          <div className="px-4 mb-16 max-w-4xl mx-auto w-full">
            <ChatInput
              input={input}
              setInput={setInput}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
              selectedFiles={selectedFiles}
              onFilesChange={setSelectedFiles}
              extendedThinking={extendedThinking}
              onExtendedThinkingChange={setExtendedThinking}
              isStreaming={stream.isStreaming}
              onStop={handleStop}
            />
          </div>
          <div className="grow" />
        </>
      ) : (
        <>
          <div className="grow overflow-hidden">
            <div className="max-w-4xl mx-auto w-full h-full">
              <ChatMessages
                messages={messages}
                messagesEndRef={messagesEndRef}
                sessionId={sessionId || ""}
                onFeedbackSubmit={handleFeedbackSubmit}
                // 選択肢カードのタップ＝通常のユーザー発言として送信（B2）。
                // ストリーミング中の多重送信はガード。
                onSendChoice={(text) => {
                  if (!stream.isStreaming) sendMessage(text)
                }}
              />
            </div>
          </div>
          <div className="flex-none pb-[env(safe-area-inset-bottom)]">
            <div className="max-w-4xl mx-auto w-full">
              {/* ツール実行中の表示（テキストが流れない数十秒の無言を可視化・2026-07-02 P3） */}
              {stream.isStreaming && stream.activityNotice && (
                <div className="flex items-center gap-2 px-4 pb-1 text-xs text-muted-foreground">
                  <span className="inline-block h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
                  {stream.activityNotice}
                </div>
              )}
              <ChatInput
                input={input}
                setInput={setInput}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                selectedFiles={selectedFiles}
                onFilesChange={setSelectedFiles}
                extendedThinking={extendedThinking}
                onExtendedThinkingChange={setExtendedThinking}
                isStreaming={stream.isStreaming}
                onStop={handleStop}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
